import React, { useEffect } from 'react';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { loginSuccess, setInitialized } from '@/state/authSlice';
import { setSession } from '@/state/sessionSlice';
import { authApi } from '@/api/auth.api';

interface AuthInitializerProps {
  children: React.ReactNode;
}

/**
 * Added based on Dinisuru's request:
 * Implements the standard In-Memory Access Token + HttpOnly Cookie Silent Refresh pattern.
 * Sensitive auth tokens (JWT and user profile) and session data are NEVER stored in localStorage.
 * On application boot (page reload or new tab), this component triggers a silent refresh
 * call to POST /api/v1/auth/refresh using the browser's HttpOnly cookie, restoring auth and session in memory.
 */
export const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const isInitialized = useAppSelector((state) => state.auth.isInitialized);

  useEffect(() => {
    let mounted = true;

    const bootstrapAuth = async () => {
      try {
        const response = await authApi.refresh();
        const data = response.data?.data;
        if (mounted && response.data?.success && data) {
          dispatch(loginSuccess(data));

          // Rehydrate operating session context in memory
          // Changed based on Dinisuru's request: Prioritize user's active location/CC over default to prevent F5 reverting context
          const comp = data.accessibleCompanies?.find(c => c.id === data.user.companyId)
                    ?? data.accessibleCompanies?.[0];
          const loc = (data.user.locationId ? data.accessibleLocations?.find(l => l.id === data.user.locationId) : null)
                   ?? data.accessibleLocations?.find(l => l.isDefault)
                   ?? data.accessibleLocations?.[0];
          const cc = (data.user.costCenterId ? data.accessibleCostCenters?.find(c => c.id === data.user.costCenterId) : null)
                  ?? data.accessibleCostCenters?.find(c => c.isDefault)
                  ?? data.accessibleCostCenters?.[0];

          if (comp) {
            dispatch(setSession({
              companyId: comp.id,
              companyCode: comp.code,
              companyName: comp.name,
              companyCurrency: comp.baseCurrencyCode,
              locationId: data.user.locationId ?? loc?.id ?? null,
              locationCode: data.user.locationCode ?? loc?.code ?? null,
              locationName: data.user.locationName ?? loc?.name ?? null,
              costCenterId: data.user.costCenterId ?? cc?.id ?? null,
              costCenterCode: data.user.costCenterCode ?? cc?.code ?? null,
              costCenterName: data.user.costCenterName ?? cc?.name ?? null,
              financialYearCode: data.user.financialYearCode ?? null,
            }));
          }
        } else if (mounted) {
          dispatch(setInitialized());
        }
      } catch {
        // No cookie present or refresh token expired -> user is unauthenticated
        if (mounted) {
          dispatch(setInitialized());
        }
      }
    };

    bootstrapAuth();

    return () => {
      mounted = false;
    };
  }, [dispatch]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-slate-400 font-medium tracking-wide">Initializing session...</span>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
